import { useCallback, useMemo, useState } from 'react';

import { NOTE_SCOPES } from '../../../services/notes.js';
import { isRichTextEmpty } from '../utils/richText.js';

const EMPTY_VALUES = Object.freeze({
  title: '',
  body: '',
  scope: NOTE_SCOPES.PUBLIC,
  attachments: [],
  urls: [],
});

/**
 * The state of the note form, shared by create and edit (CT-5528).
 *
 * Mirrors the shape of Governance's `useCreateTruthPlaneForm` — field state,
 * validity as a `useMemo` — without importing it. Title and body are required;
 * the body is judged by the TEXT it holds, not by the HTML around it, because a
 * cleared editor is `<p></p>`, not `''`.
 *
 * `invalidReason` is the i18n key of what is missing, or null. The form shows it
 * next to the disabled Save, so a blocked button always says why.
 *
 * `urls` and `attachments` are the values the request carries
 * (`string[]` and `{ drive_file_id }[]`); the picked file's NAME is kept beside
 * it, for display only.
 */
export function useNoteForm(initial = EMPTY_VALUES) {
  const [title, setTitle] = useState(initial.title ?? '');
  const [body, setBody] = useState(initial.body ?? '');
  const [scope, setScope] = useState(initial.scope ?? NOTE_SCOPES.PUBLIC);
  const [files, setFiles] = useState(initial.files ?? []);
  const [urls, setUrls] = useState(initial.urls ?? []);

  const invalidReason = useMemo(() => {
    if (title.trim() === '') return 'notes.form.invalid.title';
    if (isRichTextEmpty(body)) return 'notes.form.invalid.body';
    return null;
  }, [title, body]);

  const addFiles = useCallback((picked) => {
    setFiles((current) => {
      const known = new Set(current.map((file) => file.id));
      return [...current, ...picked.filter((file) => !known.has(file.id))];
    });
  }, []);
  const removeFile = useCallback((id) => setFiles((current) => current.filter((file) => file.id !== id)), []);

  const addUrl = useCallback((raw) => {
    const url = raw.trim();
    if (!url) return false;
    setUrls((current) => (current.includes(url) ? current : [...current, url]));
    return true;
  }, []);
  const removeUrl = useCallback((url) => setUrls((current) => current.filter((item) => item !== url)), []);

  /** The request's view of the form. */
  const toPayload = useCallback(
    () => ({
      title: title.trim(),
      body,
      scope,
      attachments: files.map((file) => ({ drive_file_id: file.id })),
      urls,
    }),
    [title, body, scope, files, urls],
  );

  return useMemo(
    () => ({
      title,
      setTitle,
      body,
      setBody,
      scope,
      setScope,
      files,
      addFiles,
      removeFile,
      urls,
      addUrl,
      removeUrl,
      isValid: invalidReason === null,
      invalidReason,
      toPayload,
    }),
    [title, body, scope, files, addFiles, removeFile, urls, addUrl, removeUrl, invalidReason, toPayload],
  );
}

export default useNoteForm;
